/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Button, IModuleProps, INodeProps, NodeTag } from '@msdyn365-commerce-modules/utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  submitContactFormAction,
  SubmitContactFormDataActionInput,
} from '../../data-actions/contact/submit-contact-form-data.action';
import { ICittaContactData } from './citta-contact.data';
import { ICittaContactProps } from './citta-contact.props.autogenerated';

interface ICittaContactFormState {
  name: string;
  emailAddress: string;
  phoneNumber: string;
  country: string;
  city: string;
  reasonForEnquiry: string;
  message: string;
  inputState: boolean;
  nameError: boolean;
  eAError: boolean;
  pNError: boolean;
  countryError: boolean;
  cityError: boolean;
  reasonEnquiryError: boolean;
  messageError: boolean;
  reqErrorMsg: boolean;
  eAValErrorMsg: boolean;
  eAValidationError: boolean;
  responseReceived: boolean;
  rCCError: boolean;
  reCaptchaChecked: boolean;
  hideFirstElement: boolean;
  selectedValue: string;
}

export interface ICittaContactViewProps extends ICittaContactProps<ICittaContactData> {
  ContactContainer: IModuleProps;
  ContactForm: INodeProps;
  Name: React.ReactNode;
  EmailAddress: React.ReactNode;
  PhoneNumber: React.ReactNode;
  Country: React.ReactNode;
  City: React.ReactNode;
  ReasonForEnquiry: React.ReactNode;
  Message: React.ReactNode;
  ErrorMessage: React.ReactNode;
  Recaptcha: React.ReactNode;
  SubmitButton: React.ReactNode;
}

/**
 *
 * CittaContact component
 * @extends {React.PureComponent<ICittaContactProps<ICittaContactData>, ICittaContactFormState>}
 */
@observer
class CittaContact extends React.PureComponent<
ICittaContactProps<ICittaContactData>,
ICittaContactFormState
> {
  public state: ICittaContactFormState = {
    name: '',
    emailAddress: '',
    phoneNumber: '',
    country: '',
    city: '',
    reasonForEnquiry: '',
    message: '',
    inputState: true,
    nameError: false,
    eAError: false,
    pNError: false,
    countryError: false,
    cityError: false,
    reasonEnquiryError: false,
    messageError: false,
    reqErrorMsg: false,
    eAValErrorMsg: false,
    eAValidationError: false,
    responseReceived: false,
    rCCError: false,
    reCaptchaChecked: false,
    hideFirstElement: false,
    selectedValue: '',
  };
  private _nameInputRef: React.RefObject<HTMLInputElement>;
  private _emailAddressInputRef: React.RefObject<HTMLInputElement>;
  private _phoneNumberInputRef: React.RefObject<HTMLInputElement>;
  private _countryInputRef: React.RefObject<HTMLInputElement>;
  private _cityInputRef: React.RefObject<HTMLInputElement>;
  private _reasonForEnquirySelectRef: React.RefObject<HTMLSelectElement>;
  private _messageInputRef: React.RefObject<HTMLTextAreaElement>;
  private _recaptchaRef: React.RefObject<ReCAPTCHA>;

  private nameValue: boolean = false;
  private emailAddressValue: boolean = false;
  private phoneNumberValue: boolean = false;
  private countryValue: boolean = false;
  private cityValue: boolean = false;
  private messageValue: boolean = false;
  private reasonEnquiryValue: boolean = false;

  private formInputValues: boolean = false;
  private requiredErrorMessage: string | undefined;
  private eAValidationErrorMessage: string | undefined;
  private recaptchaErrorMessage: string | undefined;
  private response: boolean = false;

  constructor(props: ICittaContactProps<ICittaContactData>) {
    super(props);
    this._nameInputRef = React.createRef();
    this._emailAddressInputRef = React.createRef();
    this._phoneNumberInputRef = React.createRef();
    this._countryInputRef = React.createRef();
    this._cityInputRef = React.createRef();
    this._reasonForEnquirySelectRef = React.createRef();
    this._messageInputRef = React.createRef();
    this._recaptchaRef = React.createRef();
    this._handleSend = this._handleSend.bind(this);
  }

  // tslint:disable-next-line: max-func-body-length cyclomatic-complexity
  public render(): JSX.Element {

    const formProps = {
      className: 'contact-form',
      onSubmit: this._handleSend,
      tag: 'form' as NodeTag
    };
    const viewProps = {
      ...this.props,
      ContactContainer: {
        moduleProps: this.props,
        className: 'citta-contact',
      },
      ContactForm: formProps,
      Name: this._renderName(),
      ErrorMessage: this._renderErrorMessage(),
      EmailAddress: this._renderEmailAddress(),
      PhoneNumber: this._renderPhoneNumber(),
      Country: this._renderCountry(),
      City: this._renderCity(),
      ReasonForEnquiry: this._renderReasonForEnquiry(),
      Message: this._renderMessage(),
      Recaptcha: this._renderRecaptcha(),
      SubmitButton: this._renderSubmitButton()
    };

    return this.props.renderView(viewProps) as React.ReactElement;
    // return (
    //   <div className='citta-contact'>
    //   <form className='contact-form' onSubmit={this._handleSend}>

    //     {this.props.config.reCaptchaOption ? (
    //       <div className='recaptcha'>
    //         {this.state.rCCError
    //           ? this._renderRecaptchaAlert('recaptcha-error')
    //           : ''}
    //         <ReCAPTCHA
    //           ref={this._recaptchaRef}
    //           sitekey={this.props.config.reCaptchaSiteKey!}
    //           onChange={this.onRecaptchaChange}
    //         />
    //       </div>
    //     ) : null}
    //     <div className='contact-form-send'>
    //       <Button
    //         className='contact-form-send-button'
    //         title={this.props.resources.contactSendButtonText}
    //         onClick={this._handleSend}
    //         aria-label={this.props.resources.contactSendButtonText}
    //       >
    //         {this.props.resources.contactSendButtonText}
    //       </Button>
    //     </div>
    //   </form>
    //   </div>
    // );
  }

  private _renderErrorMessage(): React.ReactNode {
    return (
      <div>
        {this.state.responseReceived && this.response === true ? (
          <div className='response-message'>
            <p className='response-message-content'>
              {this.props.resources.responseSuccessMessage}
            </p>
          </div>
        ) : null
        }
        {
          this.state.responseReceived && this.response === false ? (
            <div className='response-message'>
              <p className='response-error-message-content'>
                {this.props.resources.responseErrorMessage}
              </p>
            </div>
          ) : null
        }
      </div>
    );
  }

  private _renderName(): React.ReactNode {
    return (
      <div className='contact-form-name'>
        {this.state.nameError ? this._renderAlert('contact-form-name') : ''}
        <label id='contact-form-name__label' aria-label='name' />
        <input
          ref={this._nameInputRef}
          type='text'
          onChange={this.handleChange('nameInput')}
          className='contact-form-name-text'
          aria-label={this.props.resources.contactNamePlaceholderText}
          value={this.state.name}
          placeholder={this.props.resources.contactNamePlaceholderText}
          aria-labelledby='contact-form-name__label contact-form-name__error'
          aria-required='true'
        />
      </div>
    );
  }

  private _renderEmailAddress(): React.ReactNode {
    return (
      <div className='contact-form-emailaddress'>
        {this.state.eAError || this.state.eAValidationError
          ? this._renderAlert('contact-form-emailaddress')
          : ''}
        <label
          id='contact-form-emailaddress__label'
          aria-label='emailaddress'
        />
        <input
          ref={this._emailAddressInputRef}
          type='text'
          onChange={this.handleChange('emailAddress')}
          className='contact-form-emailaddress-text'
          aria-label={this.props.resources.contactEmailAddressPlaceholderText}
          value={this.state.emailAddress}
          placeholder={
            this.props.resources.contactEmailAddressPlaceholderText
          }
          aria-labelledby='contact-form-emailaddress__label contact-form-emailaddress__error'
          aria-required='true'
        />
      </div>
    );
  }

  private _renderPhoneNumber(): React.ReactNode {
    return (
      <div className='contact-form-phonenumber'>
        {this.state.pNError
          ? this._renderAlert('contact-form-phonenumber')
          : ''}
        <label
          id='contact-form-phonenumber__label'
          aria-label='phonenumber'
        />
        <input
          ref={this._phoneNumberInputRef}
          type='tel'
          onChange={this.handleChange('phoneNumber')}
          className='contact-form-phonenumber-text'
          aria-label={this.props.resources.contactPhoneNumberPlaceholderText}
          value={this.state.phoneNumber}
          placeholder={this.props.resources.contactPhoneNumberPlaceholderText}
          aria-labelledby='contact-form-phonenumber__label contact-form-phonenumber__error'
          aria-required='true'
        />
      </div>
    );
  }
  private _renderCountry(): React.ReactNode {
    return (
      <div className='contact-form-country'>
        {this.state.countryError
          ? this._renderAlert('contact-form-country')
          : ''}
        <label id='contact-form-country__label' aria-label='country' />
        <input
          ref={this._countryInputRef}
          type='text'
          onChange={this.handleChange('countryInput')}
          className='contact-form-country-text'
          aria-label={this.props.resources.contactCountryPlaceholderText}
          value={this.state.country}
          placeholder={this.props.resources.contactCountryPlaceholderText}
          aria-labelledby='contact-form-country__label contact-form-country__error'
          aria-required='true'
        />
      </div>
    );
  }

  private _renderCity(): React.ReactNode {
    return (
      <div className='contact-form-city'>
        {this.state.cityError ? this._renderAlert('contact-form-city') : ''}
        <label id='contact-form-city__label' aria-label='city' />
        <input
          ref={this._cityInputRef}
          type='text'
          onChange={this.handleChange('cityInput')}
          className='contact-form-city-text'
          aria-label={this.props.resources.contactCityPlaceholderText}
          value={this.state.city}
          placeholder={this.props.resources.contactCityPlaceholderText}
          aria-labelledby='contact-form-city__label contact-form-city__error'
          aria-required='true'
        />
      </div>
    );
  }

  private _renderReasonForEnquiry(): React.ReactNode {
    return (
      <div className='contact-form-reasonforenquiry'>
        {this.state.reasonEnquiryError
          ? this._renderAlert('contact-form-reasonforenquiry')
          : ''}
        <label id='contact-form-reasonforenquiry__label' />
        <select
          ref={this._reasonForEnquirySelectRef}
          className='contact-form-reasonforenquiry-text'
          aria-label={this.props.resources.contactReasonForEnquiryOption1Text}
          value={this.state.reasonForEnquiry}
          onChange={this.onChangeSelect}
          aria-labelledby='contact-form-reasonforenquiry__label contact-form-reasonforenquiry__error'
          aria-required='true'
        >
          {!this.state.hideFirstElement && (
            <option
              aria-selected='true'
              aria-hidden='true'
              value={this.props.resources.contactReasonForEnquiryOption1Text}
            >
              {this.props.resources.contactReasonForEnquiryOption1Text}
            </option>
          )}
          <option
            aria-selected={
              (this.state.selectedValue ===
                this.props.resources.contactReasonForEnquiryOption2Text &&
                'true') ||
              'false'
            }
            value={this.props.resources.contactReasonForEnquiryOption2Text}
          >
            {this.props.resources.contactReasonForEnquiryOption2Text}
          </option>
          <option
            aria-selected={
              (this.state.selectedValue ===
                this.props.resources.contactReasonForEnquiryOption3Text &&
                'true') ||
              'false'
            }
            value={this.props.resources.contactReasonForEnquiryOption3Text}
          >
            {this.props.resources.contactReasonForEnquiryOption3Text}
          </option>
          <option
            aria-selected={
              (this.state.selectedValue ===
                this.props.resources.contactReasonForEnquiryOption4Text &&
                'true') ||
              'false'
            }
            value={this.props.resources.contactReasonForEnquiryOption4Text}
          >
            {this.props.resources.contactReasonForEnquiryOption4Text}
          </option>
          <option
            aria-selected={
              (this.state.selectedValue ===
                this.props.resources.contactReasonForEnquiryOption5Text &&
                'true') ||
              'false'
            }
            value={this.props.resources.contactReasonForEnquiryOption5Text}
          >
            {this.props.resources.contactReasonForEnquiryOption5Text}
          </option>
          <option
            aria-selected={
              (this.state.selectedValue ===
                this.props.resources.contactReasonForEnquiryOption6Text &&
                'true') ||
              'false'
            }
            value={this.props.resources.contactReasonForEnquiryOption6Text}
          >
            {this.props.resources.contactReasonForEnquiryOption6Text}
          </option>
          <option
            aria-selected={
              (this.state.selectedValue ===
                this.props.resources.contactReasonForEnquiryOption7Text &&
                'true') ||
              'false'
            }
            value={this.props.resources.contactReasonForEnquiryOption7Text}
          >
            {this.props.resources.contactReasonForEnquiryOption7Text}
          </option>
          <option
            aria-selected={
              (this.state.selectedValue ===
                this.props.resources.contactReasonForEnquiryOption8Text &&
                'true') ||
              'false'
            }
            value={this.props.resources.contactReasonForEnquiryOption8Text}
          >
            {this.props.resources.contactReasonForEnquiryOption8Text}
          </option>
        </select>
      </div>
    );
  }
  private _renderMessage(): React.ReactNode {
    return (
      <div className='contact-form-message'>
        {this.state.messageError
          ? this._renderAlert('contact-form-message')
          : ''}
        <label id='contact-form-message__label' aria-label='message' />
        <textarea
          ref={this._messageInputRef}
          onChange={this.onChangeTextArea}
          className='contact-form-message-text'
          aria-label={this.props.resources.contactMessagePlaceholderText}
          value={this.state.message}
          placeholder={this.props.resources.contactMessagePlaceholderText}
          aria-labelledby='contact-form-message__label contact-form-message__error'
          aria-required='true'
        />
      </div>
    );
  }

  private _renderRecaptcha(): React.ReactNode {
    return (
      <div>
        {
          this.props.config.reCaptchaOption ? <div className='recaptcha'>
            {this.state.rCCError ? this._renderRecaptchaAlert('recaptcha-error') : ''}
            <ReCAPTCHA
              ref={this._recaptchaRef}
              sitekey={this.props.config.reCaptchaSiteKey!}
              onChange={this.onRecaptchaChange}
            />
          </div> : null
        }
      </div>
    );
  }

  private _renderSubmitButton(): React.ReactNode {
    return (
      <div className='contact-form-send'>
        <Button
          className='contact-form-send-button'
          title={this.props.resources.contactSendButtonText}
          onClick={this._handleSend}
          aria-label={this.props.resources.contactSendButtonText}
        >
          {this.props.resources.contactSendButtonText}
        </Button>
      </div>
    );
  }

  private onRecaptchaChange = (value: string | null) => {
    if (value) {
      this.setState({ reCaptchaChecked: true, rCCError: false });
    } else {
      this.setState({ reCaptchaChecked: false });
    }
  };

  private handleChange = (inputText: string) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    switch (inputText) {
      case 'nameInput': {
        this.setState({ name: value, nameError: false, reqErrorMsg: false });
        break;
      }
      case 'emailAddress': {
        this.setState({
          emailAddress: value,
          eAError: false,
          reqErrorMsg: false,
          eAValErrorMsg: false,
          eAValidationError: false,
        });
        break;
      }
      case 'phoneNumber': {
        this.setState({
          phoneNumber: value,
          pNError: false,
          reqErrorMsg: false,
        });
        break;
      }
      case 'countryInput': {
        this.setState({
          country: value,
          countryError: false,
          reqErrorMsg: false,
        });
        break;
      }
      case 'cityInput': {
        this.setState({ city: value, cityError: false, reqErrorMsg: false });
        break;
      }
      default:
        null;
    }
  };

  private onChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    this.setState({
      reasonForEnquiry: e.target.value || '',
      reasonEnquiryError: false,
      reqErrorMsg: false,
      hideFirstElement: true,
      selectedValue: e.target.value,
    });
  };

  private onChangeTextArea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.setState({
      message: e.target.value || '',
      reqErrorMsg: false,
      messageError: false,
    });
  };

  private isEmailValid = (email: string): boolean => {
    const regex = /^(([^<>()\[\]\\.,;:\s@']+(\.[^<>()\[\]\\.,;:\s@']+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return regex.test(email);
  };

  private _renderAlert(className: string): JSX.Element {
    const inputBoxErrors =
      this.state.nameError ||
      this.state.eAError ||
      this.state.pNError ||
      this.state.countryError ||
      this.state.cityError ||
      this.state.reasonEnquiryError ||
      this.state.messageError;
    if (inputBoxErrors) {
      this.requiredErrorMessage = this.props.resources.requiredTextErrorMessage;
    }
    if (this.state.emailAddress !== '' && this.state.eAValidationError) {
      this.eAValidationErrorMessage = this.props.resources.eAValidationErrorMessage;
    }
    const reqError = this.state.reqErrorMsg;
    const eAValError = this.state.eAValErrorMsg;
    return (
      <>
        {reqError && (
          <span
            className={`${className}__alert`}
            role='alert'
            aria-live='assertive'
          >
            {this.requiredErrorMessage && (
              <>
                <span
                  className={`${className}__alert-icon`}
                  aria-hidden='true'
                />
                {this.requiredErrorMessage}
              </>
            )}
          </span>
        )}
        {eAValError && (
          <span
            className={`${className}__alert`}
            role='alert'
            aria-live='assertive'
          >
            {this.eAValidationErrorMessage && (
              <>
                <span
                  className={`${className}__alert-icon`}
                  aria-hidden='true'
                />
                {this.eAValidationErrorMessage}
              </>
            )}
          </span>
        )}
      </>
    );
  }

  private _renderRecaptchaAlert(className: string): JSX.Element {
    if (this.props.config.reCaptchaOption) {
      if (!this.state.reCaptchaChecked) {
        this.recaptchaErrorMessage = this.props.resources.recaptchaErrorMessage;
      }
    }

    const rCCError = this.state.rCCError;
    return (
      <>
        {rCCError && (
          <span
            className={`${className}__alert`}
            role='alert'
            aria-live='assertive'
          >
            {this.recaptchaErrorMessage && (
              <>
                <span
                  className={`${className}__alert-icon`}
                  aria-hidden='true'
                />
                {this.recaptchaErrorMessage}
              </>
            )}
          </span>
        )}
      </>
    );
  }

  // tslint:disable-next-line: max-func-body-length cyclomatic-complexity
  private async _handleSend(
    e:
      | React.MouseEvent<HTMLElement>
      | React.KeyboardEvent<HTMLElement>
      | React.FormEvent<HTMLFormElement>
  ): Promise<void> {
    this.nameValue =
      this._nameInputRef &&
        this._nameInputRef.current &&
        this._nameInputRef.current.value
        ? true
        : false;
    this.emailAddressValue =
      this._emailAddressInputRef &&
        this._emailAddressInputRef.current &&
        this._emailAddressInputRef.current.value
        ? true
        : false;
    this.phoneNumberValue =
      this._phoneNumberInputRef &&
        this._phoneNumberInputRef.current &&
        this._phoneNumberInputRef.current.value
        ? true
        : false;
    this.countryValue =
      this._countryInputRef &&
        this._countryInputRef.current &&
        this._countryInputRef.current.value
        ? true
        : false;
    this.cityValue =
      this._cityInputRef &&
        this._cityInputRef.current &&
        this._cityInputRef.current.value
        ? true
        : false;
    const reasonEnquiryInnerText =
      this._reasonForEnquirySelectRef &&
      this._reasonForEnquirySelectRef.current &&
      this._reasonForEnquirySelectRef.current.selectedOptions[0].innerText;
    this.reasonEnquiryValue =
      reasonEnquiryInnerText ===
        this.props.resources.contactReasonForEnquiryOption1Text
        ? false
        : true;
    this.messageValue =
      this._messageInputRef &&
        this._messageInputRef.current &&
        this._messageInputRef.current.value
        ? true
        : false;
    const eAVal = this._emailAddressInputRef.current!.value;

    const nA =
      this._nameInputRef &&
        this._nameInputRef.current &&
        this._nameInputRef.current.value
        ? this._nameInputRef.current.value
        : '';
    const eA =
      this._emailAddressInputRef &&
        this._emailAddressInputRef.current &&
        this._emailAddressInputRef.current.value
        ? this._emailAddressInputRef.current.value
        : '';
    const pN =
      this._phoneNumberInputRef &&
        this._phoneNumberInputRef.current &&
        this._phoneNumberInputRef.current.value
        ? this._phoneNumberInputRef.current.value
        : '';
    const cV =
      this._countryInputRef &&
        this._countryInputRef.current &&
        this._countryInputRef.current.value
        ? this._countryInputRef.current.value
        : '';
    const cI =
      this._cityInputRef &&
        this._cityInputRef.current &&
        this._cityInputRef.current.value
        ? this._cityInputRef.current.value
        : '';
    const rE = this.state.reasonForEnquiry ? this.state.reasonForEnquiry : '';
    const mV =
      this._messageInputRef &&
        this._messageInputRef.current &&
        this._messageInputRef.current.value
        ? this._messageInputRef.current.value
        : '';

    if (!this.nameValue) {
      this.setState({ nameError: true, reqErrorMsg: true });
    } else if (!this.emailAddressValue) {
      this.setState({ eAError: true, reqErrorMsg: true });
    } else if (!this.isEmailValid(eAVal)) {
      this.setState({ eAValidationError: true, eAValErrorMsg: true });
    } else if (!this.phoneNumberValue) {
      this.setState({ pNError: true, reqErrorMsg: true });
    } else if (!this.countryValue) {
      this.setState({ countryError: true, reqErrorMsg: true });
    } else if (!this.cityValue) {
      this.setState({ cityError: true, reqErrorMsg: true });
    } else if (!this.reasonEnquiryValue) {
      this.setState({ reasonEnquiryError: true, reqErrorMsg: true });
    } else if (!this.messageValue) {
      this.setState({ messageError: true, reqErrorMsg: true });
    } else if (
      this.props.config.reCaptchaOption &&
      !this.state.reCaptchaChecked
    ) {
      this.setState({ rCCError: true });
    } else {
      this.formInputValues =
        this.nameValue &&
          this.emailAddressValue &&
          this.phoneNumberValue &&
          this.countryValue &&
          this.cityValue &&
          this.messageValue
          ? true
          : false;
      if (this.formInputValues) {
        try {
          const result = await submitContactFormAction(
            new SubmitContactFormDataActionInput(nA, eA, pN, cV, cI, rE, mV),
            this.props.context.actionContext
          );
          this.response = result.success;
          if (this.response === true) {
            this.setState({
              name: '',
              emailAddress: '',
              phoneNumber: '',
              country: '',
              city: '',
              reasonForEnquiry: '',
              message: '',
              responseReceived: true,
              reqErrorMsg: false,
              eAValErrorMsg: false,
              rCCError: false,
              reCaptchaChecked: false,
              hideFirstElement:false
            });
            this._recaptchaRef.current?.reset();
          } else {
            this.setState({ responseReceived: true });
          }
        } catch {
          console.log('Please fill the form!');
        }
      }
    }
  }
}

export default CittaContact;
